import { Check, ChevronRight } from "lucide-react";
import { motion } from "motion/react";

import { sheetUnfoldSpring } from "@/utils/animations";
import { useTranslation } from "react-i18next";

import { CampaignImage } from "@/components/campaign-card/campaign-image";
import { Typography } from "@/components/ui/typography";
import type { SwitchableCampaign } from "@/hooks/use-switchable-campaigns";
import { cn } from "@/lib/utils";
import { campaignHook, campaignKey, matchesKey } from "@/campaigns";
import { campaignDisplayTitle } from "@/utils/campaign-helpers";
import { CampaignHookLine } from "@/components/campaign-hook-line";

/**
 * The rows that unfold out of the mobile in-game campaign dock when the player switches.
 *
 * Rendered INSIDE the dock's raised surface (not a portal): the selected campaign stays where it
 * always is, in the bar at the bottom, and the alternatives rise out of it — one layer, visibly
 * above the dimmed content. Each row gets the room the old pill dropdown never had: cover, title on two
 * lines, type and time left. Height-animates with the same spring as the desktop dropdown.
 */
export const CampaignSwitcherList = ({
  campaigns,
  selectedKey,
  onSelect,
  peek = false,
}: {
  campaigns: SwitchableCampaign[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  /** The first-open hint: rise only far enough to show the top row's edge, then fold again —
   *  a glimpse that there is a list in here, not an open list. Not interactive while peeking. */
  peek?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <motion.ul
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: peek ? 44 : "auto", opacity: 1 }}
      aria-hidden={peek || undefined}
      inert={peek || undefined}
      exit={{
        height: 0,
        opacity: 0,
        transition: { duration: 0.12, ease: [0.4, 0, 1, 1] },
      }}
      // Settled spring (see sheetUnfoldSpring): the list lives inside an overflow-hidden sheet,
      // where the desktop dropdown's bouncy overshoot clips the last row.
      transition={{ height: sheetUnfoldSpring, opacity: { duration: 0.1 } }}
      className="flex flex-col overflow-hidden"
    >
      {campaigns.map(({ campaign, owned }) => {
        const key = campaignKey(campaign);
        const current =
          selectedKey != null && matchesKey(campaign, selectedKey);
        const finished = campaign.status === "FI";
        const hook = campaignHook(owned, t);
        return (
          <li key={key} className="border-b border-border/40">
            {/* A plain list of buttons ("list, 6 items"): the widget has no listbox keyboard
                handling (arrows, roving focus), so it must not promise one. The current campaign
                is `aria-current` where it is listed (the side dock). */}
            <button
              type="button"
              aria-current={current || undefined}
              onClick={() => onSelect(key)}
              className={cn(
                "flex w-full cursor-pointer items-center gap-3.5 px-4 py-3 text-left transition-colors",
                current
                  ? "bg-background-card-surface/60"
                  : "active:bg-background-card-surface/40"
              )}
            >
              <CampaignImage
                campaign={campaign}
                hideTitle
                hideDuration
                className={cn(
                  "size-11 shrink-0 rounded-xl",
                  finished && "opacity-70 grayscale"
                )}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                {/* Same two lines as the bar: name + time left, then the hook. */}
                <div className="flex min-w-0 items-center gap-2 font-body text-xs text-muted-foreground">
                  <Typography
                    tag="span"
                    className={cn(
                      "min-w-0 flex-1 truncate text-[13px] leading-tight font-bold",
                      finished ? "text-muted-foreground" : "text-foreground"
                    )}
                  >
                    {campaignDisplayTitle(campaign, t)}
                  </Typography>
                </div>
                {/* One line like the bar: a long prize glides rather than breaking the row. */}
                {hook && <CampaignHookLine hook={hook} className="min-w-0" />}
              </div>
              {current ? (
                <Check
                  className="size-5 shrink-0 text-primary"
                  aria-hidden="true"
                />
              ) : (
                <ChevronRight
                  className="size-5 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              )}
            </button>
          </li>
        );
      })}
    </motion.ul>
  );
};
